import React, { useState, Component } from 'react';
import { ShieldAlertIcon, LockIcon, FilterIcon, EditIcon, PlayIcon, GitBranchIcon, CloudIcon, ArrowRightIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, UserIcon, BotIcon, ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
// Types
export type AuditCategory = 'field_change' | 'action' | 'rule_evaluation' | 'workflow_transition' | 'integration';
export type ActorType = 'user' | 'system';
export type RuleResult = 'passed' | 'failed' | 'warning';
export type IntegrationDirection = 'inbound' | 'outbound';
// Base audit entry
interface BaseAuditEntry {
  id: string;
  timestamp: string;
  actor: string;
  actorType: ActorType;
}
// Field change entry
export interface FieldChangeEntry extends BaseAuditEntry {
  category: 'field_change';
  fieldName: string;
  oldValue: string;
  newValue: string;
}
// Action entry
export interface ActionEntry extends BaseAuditEntry {
  category: 'action';
  actionType: string;
  targetEntity: string;
  description: string;
}
// Rule evaluation entry
export interface RuleEvaluationEntry extends BaseAuditEntry {
  category: 'rule_evaluation';
  ruleId: string;
  ruleVersion: string;
  result: RuleResult;
  details: string;
}
// Workflow transition entry
export interface WorkflowTransitionEntry extends BaseAuditEntry {
  category: 'workflow_transition';
  previousState: string;
  newState: string;
  triggeredBy: string;
}
// Integration entry
export interface IntegrationEntry extends BaseAuditEntry {
  category: 'integration';
  integrationName: string;
  requestId: string;
  responseStatus: string;
  direction: IntegrationDirection;
}
// Union type for all audit entries
export type AuditEntry = FieldChangeEntry | ActionEntry | RuleEvaluationEntry | WorkflowTransitionEntry | IntegrationEntry;
export interface RegulatoryAuditTrailProps {
  entityType: string;
  entityId: string;
  auditEntries: AuditEntry[];
  hasAuditAccess: boolean;
  locale?: string;
}
// Category configuration
const categoryConfig: Record<AuditCategory, {
  color: string;
  bgColor: string;
  icon: React.ElementType;
  label: string;
}> = {
  field_change: {
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    icon: EditIcon,
    label: 'Field Change'
  },
  action: {
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    icon: PlayIcon,
    label: 'Action'
  },
  rule_evaluation: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    icon: ShieldAlertIcon,
    label: 'Rule Evaluation'
  },
  workflow_transition: {
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-100',
    icon: GitBranchIcon,
    label: 'Workflow'
  },
  integration: {
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-100',
    icon: CloudIcon,
    label: 'Integration'
  }
};
// Sample mock data for demonstration
export const sampleInvoiceAuditEntries: AuditEntry[] = [{
  id: 'aud-001',
  timestamp: '2024-03-04T14:32:15Z',
  actor: 'ERP Integration Service',
  actorType: 'system',
  category: 'integration',
  integrationName: 'SAP S/4HANA',
  requestId: 'REQ-2024030414321500',
  responseStatus: '200 OK',
  direction: 'outbound'
}, {
  id: 'aud-002',
  timestamp: '2024-03-04T14:15:30Z',
  actor: 'Sarah Johnson',
  actorType: 'user',
  category: 'action',
  actionType: 'APPROVAL_GRANTED',
  targetEntity: 'INV-2024-0892',
  description: 'Invoice approved for payment'
}, {
  id: 'aud-003',
  timestamp: '2024-03-04T14:15:28Z',
  actor: 'Workflow Engine',
  actorType: 'system',
  category: 'workflow_transition',
  previousState: 'Pending Approval',
  newState: 'Approved',
  triggeredBy: 'User Approval Action'
}, {
  id: 'aud-004',
  timestamp: '2024-03-04T11:45:00Z',
  actor: 'Matching Engine',
  actorType: 'system',
  category: 'rule_evaluation',
  ruleId: 'RULE-TOLERANCE-001',
  ruleVersion: '2.1.0',
  result: 'passed',
  details: 'Invoice amount variance 0.5% within 2% tolerance threshold'
}, {
  id: 'aud-005',
  timestamp: '2024-03-04T11:44:55Z',
  actor: 'Matching Engine',
  actorType: 'system',
  category: 'rule_evaluation',
  ruleId: 'RULE-QTY-MATCH-001',
  ruleVersion: '1.3.0',
  result: 'passed',
  details: 'Quantity matched: Invoice 100 units = GRN 100 units'
}, {
  id: 'aud-006',
  timestamp: '2024-03-04T11:30:00Z',
  actor: 'Workflow Engine',
  actorType: 'system',
  category: 'workflow_transition',
  previousState: 'Matching',
  newState: 'Pending Approval',
  triggeredBy: 'Matching Completed'
}, {
  id: 'aud-007',
  timestamp: '2024-03-04T11:15:10Z',
  actor: 'Validation Service',
  actorType: 'system',
  category: 'rule_evaluation',
  ruleId: 'RULE-DUPLICATE-001',
  ruleVersion: '1.0.0',
  result: 'passed',
  details: 'No duplicate invoice detected in system'
}, {
  id: 'aud-008',
  timestamp: '2024-03-04T10:50:00Z',
  actor: 'John Smith',
  actorType: 'user',
  category: 'field_change',
  fieldName: 'TaxAmount',
  oldValue: '$2,400.00',
  newValue: '$2,450.00'
}, {
  id: 'aud-009',
  timestamp: '2024-03-04T10:45:30Z',
  actor: 'Vendor Matching Service',
  actorType: 'system',
  category: 'field_change',
  fieldName: 'VendorId',
  oldValue: '(unassigned)',
  newValue: 'VEN-00234'
}, {
  id: 'aud-010',
  timestamp: '2024-03-04T10:21:00Z',
  actor: 'Email Ingestion Service',
  actorType: 'system',
  category: 'integration',
  integrationName: 'Email Gateway',
  requestId: 'MSG-2024030410210000',
  responseStatus: 'Received',
  direction: 'inbound'
}, {
  id: 'aud-011',
  timestamp: '2024-03-04T10:21:00Z',
  actor: 'System',
  actorType: 'system',
  category: 'action',
  actionType: 'DOCUMENT_CREATED',
  targetEntity: 'INV-2024-0892',
  description: 'Invoice document created from email attachment'
}];
export function RegulatoryAuditTrail({
  entityType,
  entityId,
  auditEntries,
  hasAuditAccess,
  locale = 'en-US'
}: RegulatoryAuditTrailProps) {
  const [activeFilter, setActiveFilter] = useState<AuditCategory | 'all'>('all');
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };
  // Access denied state
  if (!hasAuditAccess) {
    return <div className="bg-gray-50 rounded-lg border border-gray-200" data-id="element-2113">
        <div className="px-6 py-12 text-center" data-id="element-2114">
          <div className="mx-auto h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-2115">
            <LockIcon className="h-8 w-8 text-gray-400" data-id="element-2116" />
          </div>
          <h3 className="mt-4 text-sm font-medium text-gray-900" data-id="element-2117">
            Access Restricted
          </h3>
          <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-2118">
            Regulatory Audit Trail access is restricted. Contact your
            administrator to request access.
          </p>
        </div>
      </div>;
  }
  const filteredEntries = activeFilter === 'all' ? auditEntries : auditEntries.filter(entry => entry.category === activeFilter);
  const sortedEntries = [...filteredEntries].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  const filterOptions: {
    value: AuditCategory | 'all';
    label: string;
  }[] = [{
    value: 'all',
    label: 'All'
  }, {
    value: 'field_change',
    label: 'Field Changes'
  }, {
    value: 'action',
    label: 'Actions'
  }, {
    value: 'rule_evaluation',
    label: 'Rules'
  }, {
    value: 'workflow_transition',
    label: 'Workflow'
  }, {
    value: 'integration',
    label: 'Integration'
  }];
  const renderEntryDetails = (entry: AuditEntry) => {
    switch (entry.category) {
      case 'field_change':
        return <div className="flex items-center space-x-2 text-sm" data-id="element-2119">
            <span className="font-medium text-gray-700" data-id="element-2120">
              {entry.fieldName}:
            </span>
            <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded line-through" data-id="element-2121">
              {entry.oldValue || '(empty)'}
            </span>
            <ArrowRightIcon className="h-4 w-4 text-gray-400" data-id="element-2122" />
            <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded" data-id="element-2123">
              {entry.newValue}
            </span>
          </div>;
      case 'action':
        return <div className="text-sm" data-id="element-2124">
            <span className="font-mono text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded" data-id="element-2125">
              {entry.actionType}
            </span>
            <span className="ml-2 text-gray-600" data-id="element-2126">{entry.description}</span>
            <span className="ml-2 text-gray-400" data-id="element-2127">→ {entry.targetEntity}</span>
          </div>;
      case 'rule_evaluation':
        return <div className="text-sm" data-id="element-2128">
            <div className="flex items-center space-x-2" data-id="element-2129">
              <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded" data-id="element-2130">
                {entry.ruleId}
              </span>
              <span className="text-xs text-gray-400" data-id="element-2131">
                v{entry.ruleVersion}
              </span>
              {entry.result === 'passed' && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2132">
                  <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-2133" />
                  Passed
                </span>}
              {entry.result === 'failed' && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-2134">
                  <XCircleIcon className="h-3 w-3 mr-1" data-id="element-2135" />
                  Failed
                </span>}
              {entry.result === 'warning' && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-2136">
                  <AlertTriangleIcon className="h-3 w-3 mr-1" data-id="element-2137" />
                  Warning
                </span>}
            </div>
            <p className="mt-1 text-gray-600" data-id="element-2138">{entry.details}</p>
          </div>;
      case 'workflow_transition':
        return <div className="flex items-center space-x-2 text-sm" data-id="element-2139">
            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded" data-id="element-2140">
              {entry.previousState}
            </span>
            <ArrowRightIcon className="h-4 w-4 text-gray-400" data-id="element-2141" />
            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded font-medium" data-id="element-2142">
              {entry.newState}
            </span>
            <span className="text-gray-400 text-xs" data-id="element-2143">
              (triggered by {entry.triggeredBy})
            </span>
          </div>;
      case 'integration':
        return <div className="text-sm" data-id="element-2144">
            <div className="flex items-center space-x-2" data-id="element-2145">
              <span className="font-medium text-gray-700" data-id="element-2146">
                {entry.integrationName}
              </span>
              {entry.direction === 'inbound' ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-700" data-id="element-2147">
                  <ArrowDownIcon className="h-3 w-3 mr-1" data-id="element-2148" />
                  Inbound
                </span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700" data-id="element-2149">
                  <ArrowUpIcon className="h-3 w-3 mr-1" data-id="element-2150" />
                  Outbound
                </span>}
              <span className={`px-2 py-0.5 rounded text-xs ${entry.responseStatus.startsWith('2') ? 'bg-green-100 text-green-700' : entry.responseStatus.startsWith('4') || entry.responseStatus.startsWith('5') ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-2151">
                {entry.responseStatus}
              </span>
            </div>
            <p className="mt-1 text-gray-400 text-xs font-mono" data-id="element-2152">
              Request ID: {entry.requestId}
            </p>
          </div>;
    }
  };
  return <div className="bg-white rounded-lg border border-gray-200" data-id="element-2153">
      <div className="px-6 py-4 border-b border-gray-200" data-id="element-2154">
        <div className="flex items-center justify-between" data-id="element-2155">
          <div data-id="element-2156">
            <h3 className="text-base font-semibold text-gray-900 flex items-center" data-id="element-2157">
              <ShieldAlertIcon className="h-5 w-5 mr-2 text-indigo-600" data-id="element-2158" />
              Regulatory Audit Trail
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-2159">
              Detailed audit records for {entityType} {entityId}
            </p>
          </div>
          <span className="text-xs text-gray-400" data-id="element-2160">
            {sortedEntries.length} records
          </span>
        </div>

        {/* Filter tabs */}
        <div className="mt-4 flex items-center space-x-1" data-id="element-2161">
          <FilterIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-2162" />
          {filterOptions.map(option => <button key={option.value} onClick={() => setActiveFilter(option.value)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${activeFilter === option.value ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-2163">
              {option.label}
            </button>)}
        </div>
      </div>

      {sortedEntries.length === 0 ? <div className="px-6 py-12 text-center" data-id="element-2164">
          <ShieldAlertIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-2165" />
          <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-2166">
            No audit records
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2167">
            {activeFilter === 'all' ? 'No audit entries have been recorded yet.' : `No ${activeFilter.replace('_', ' ')} entries found.`}
          </p>
        </div> : <div className="overflow-x-auto" data-id="element-2168">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2169">
            <thead className="bg-gray-50" data-id="element-2170">
              <tr data-id="element-2171">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2172">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2173">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2174">
                  Actor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2175">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2176">
              {sortedEntries.map(entry => {
            const config = categoryConfig[entry.category];
            const IconComponent = config.icon;
            return <tr key={entry.id} className="hover:bg-gray-50" data-id="element-2177">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2178">
                      <time dateTime={entry.timestamp} data-id="element-2179">
                        {formatTimestamp(entry.timestamp)}
                      </time>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2180">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`} data-id="element-2181">
                        <IconComponent className="h-3 w-3 mr-1" data-id="element-2182" />
                        {config.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2183">
                      <div className="flex items-center text-sm" data-id="element-2184">
                        {entry.actorType === 'user' ? <UserIcon className="h-4 w-4 text-gray-400 mr-1.5" data-id="element-2185" /> : <BotIcon className="h-4 w-4 text-gray-400 mr-1.5" data-id="element-2186" />}
                        <span className="text-gray-900" data-id="element-2187">{entry.actor}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-2188">{renderEntryDetails(entry)}</td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
    </div>;
}